import {
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useState,
} from 'react'

const MIN_COLUMN_WIDTH = 60

// Widths stay null until the first drag, so an untouched table keeps the exact
// layout its CSS gives it. The first drag measures what is on screen and locks
// those pixels in, which is why nothing jumps when you grab a handle.
export const useResizableColumns = (
  headerRowRef: RefObject<HTMLTableRowElement | null>,
  columnKeys: string[],
) => {
  const [widths, setWidths] = useState<Record<string, number> | null>(null)
  const [resizingKey, setResizingKey] = useState<string | null>(null)

  const measureColumns = () => {
    const headerCells = headerRowRef.current?.children
    if (!headerCells) return null

    const measured: Record<string, number> = {}
    columnKeys.forEach((key, index) => {
      const cell = headerCells[index]
      if (cell) measured[key] = cell.getBoundingClientRect().width
    })
    return measured
  }

  const startResize = (
    columnKey: string,
    event: ReactPointerEvent<HTMLElement>,
  ) => {
    const startWidths = widths ?? measureColumns()
    const startWidth = startWidths?.[columnKey]
    if (!startWidths || startWidth === undefined) return

    event.preventDefault()
    event.stopPropagation()

    const handle = event.currentTarget
    const startX = event.clientX

    handle.setPointerCapture(event.pointerId)
    setWidths(startWidths)
    setResizingKey(columnKey)

    const resize = (move: PointerEvent) => {
      setWidths({
        ...startWidths,
        [columnKey]: Math.max(
          MIN_COLUMN_WIDTH,
          startWidth + move.clientX - startX,
        ),
      })
    }

    const stopResize = () => {
      handle.removeEventListener('pointermove', resize)
      handle.removeEventListener('pointerup', stopResize)
      handle.removeEventListener('pointercancel', stopResize)
      setResizingKey(null)
    }

    handle.addEventListener('pointermove', resize)
    handle.addEventListener('pointerup', stopResize)
    handle.addEventListener('pointercancel', stopResize)
  }

  const totalWidth = widths
    ? Object.values(widths).reduce((total, width) => total + width, 0)
    : null

  return { widths, totalWidth, resizingKey, startResize }
}
